import React from 'react';
import { Helmet } from 'react-helmet-async';
import PropTypes from 'prop-types';
import { Product } from '@utils/product';
import { SEO } from '@config';

// https://schema.org/BookFormatType
const getFormat = (type) => {
  switch (type) {
  case 'Hardcover':
    return 'http://schema.org/Hardcover';
  case 'Luisterboek':
    return 'http://schema.org/AudiobookFormat';
  case 'eBook':
    return 'http://schema.org/EBook';
  default:
    return 'http://schema.org/Paperback';
  }
};

// https://schema.org/ItemAvailability
const getAvailability = (offered) => {
  switch (offered) {
  case 0:
    return 'https://schema.org/OutOfStock';
  case 1:
    return 'http://schema.org/InStock';
  case 2:
    return 'https://schema.org/PreOrder';
  case 4:
    return 'https://schema.org/InStoreOnly';
  default:
    return 'https://schema.org/OutOfStock';
  }
};

const ProductData = (props) => {
  const product = new Product(props.product || {});
  const baseUrl = SEO.organization.url || '';

  // The base object for all products, regardless of type
  const schema = {
    '@context': 'http://schema.org',
    '@id': baseUrl + product.url,
    name: product.name,
    url: baseUrl + product.url,
    keywords: product.keywords,
    description: (product.shortDescription || product.longDescription),
    image: [
      [product.images?.frontImage, product.images?.backImage, product.images?.inlayImage]
        .filter(Boolean)
        .map(i => baseUrl + i)
        .join(','),
    ],
    offers: {
      '@type': 'Offer',
      price: product.price?.regular?.[0]?.amount,
      priceCurrency: 'EUR',
      availability: getAvailability(product.offered),
      itemCondition: 'https://schema.org/NewCondition',
    },
  };

  /*
   * Always add the AggregateRating attribute
   * even when there is no rating.
   */
  const ratingValue = product.rating ? product.rating.toString() : undefined;
  const reviewCount = (product.reviewCount && product.reviewCount > 0) ? product.reviewCount.toString() : undefined;
  if (ratingValue) {
    schema.AggregateRating = {
      '@type': 'AggregateRating',
      ratingValue,
      reviewCount,
      worstRating: '1',
      bestRating: '5',
    };
  }


  // Check if other editions exist and add them as workExamples
  const workExample = [];
  (product?.otherEditionsJson?.items || []).forEach((edition) => {
    if (edition.active) {
      return;
    }

    if (product.isBook) {
      workExample.push({
        '@context': 'http://schema.org',
        '@type': 'Book',
        '@id': edition.url,
        isbn: edition.url.match(/\d+/gi)?.pop(),
        bookFormat: getFormat(edition.type),
        inLanguage: product.languageAlpha2,
      });
    }
  });

  /*
   * Create an array of persons, which can be used for the author attribute,
   * but also for artists, characters or actors
   */
  const persons = product.persons.map(person => ({
    '@type': 'Person',
    name: person.name,
  }));

  /*
   * Add specific attributes if the product is a book
   * otherwise fall back to a default product
   */
  if (product.isBook) {
    schema['@type'] = 'Book';
    schema.inLanguage = product.languageAlpha2;
    schema.workExample = workExample;
    schema.thumbnailUrl = baseUrl + product.images?.Image;
    schema.datePublished = product.releaseDate?.match(/\d{4}/g)?.pop();
    schema.numberOfPages = product.pages;
    schema.bookFormat = getFormat(product.type);
    schema.genre = product.category;
    schema.isbn = product.SKU;
    schema.author = persons;
    schema.publisher = {
      '@type': 'Organization',
      name: product.brand,
      // url: product.brandUrl, // Uncomment when the EDP is enabled
    };
  } else {
    // Default to standard product
    schema['@type'] = 'Product';
    schema.gtin13 = product.SKU;
    schema.releaseDate = product.releaseDate?.match(/\d{4}/g)?.pop();
    schema.brand = {
      '@type': 'Organization',
      name: product.brand,
      // url: product.brandUrl, // Uncomment when the EDP is enabled
    };
  }

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

ProductData.propTypes = {
  product: PropTypes.object,
};

export default ProductData;
